import {
   createContext,
   useContext,
   useEffect,
   useState,
   ReactNode,
   Dispatch,
   SetStateAction,
} from "react";
import { getListOfLists } from "../../services/restAPI/ListsV3";
import { Message } from "../../components/BP2Components";
import { listElement, QueryObject } from "../../components/Widgets/ListBuilder/ListBuildertypes";
import { reverseList } from "../../components/Widgets/ListBuilder/ListBuilderScripts";

interface PagedList<T> {
   data: T[];
   page: number;
   size: number;
   total: number;
}

interface SearchDataInterface {
   column: string | undefined;
   userInput: string | undefined;
}

interface ListBuilderContextInterface {
   mainList: PagedList<listElement>;
   setMainList: (list: PagedList<listElement>) => void;
   page: number;
   setPage: (page: number) => void;
   searchData: SearchDataInterface;
   setSearchData: (data: SearchDataInterface) => void;
   queryObject: QueryObject;
   setQueryObject: Dispatch<SetStateAction<QueryObject>>;
   currentTab: number;
   setCurrentTab: Dispatch<SetStateAction<number>>;
   refreshList: () => void;
   removeFromMainList: (listId: string) => void;
}

const ListBuilderContext = createContext<ListBuilderContextInterface | null>(null);

export const useListBuilder = () => {
   const ctx = useContext(ListBuilderContext);
   if (!ctx) throw new Error("useListBuilder must be used within ListBuilderProvider");
   return ctx;
};

export const ListBuilderProvider = ({ children }: { children: ReactNode }) => {
   const [page, setPage] = useState<number>(1);
   const [currentTab, setCurrentTab] = useState<number>(1); // TODO: check if we use it anywhere
   // const [totalPages, setTotalPages] = useState<number>(1);
   const [queryObject, setQueryObject] = useState<QueryObject>({
      type: "",
      properties: {},
      subQueries: [],
      size: 10,
   });

   const [searchData, setSearchData] = useState<SearchDataInterface>({
      column: undefined,
      userInput: undefined,
   });

   const [mainList, setMainList] = useState<PagedList<listElement>>({
      data: [],
      page: 1,
      size: 8,
      total: 0,
   });

   const fetchLists = async (uiPage: number) => {
      try {
         const res = await getListOfLists(undefined, undefined, searchData);
         if (!res?.data) return;

         let fullList = reverseList(res.data.data);

         if (searchData.column && searchData.userInput) {
            const column = searchData.column;
            const userInput = searchData.userInput.toLowerCase();
            fullList = fullList.filter((item: any) =>
               item[column]?.toString().toLowerCase().includes(userInput),
            );
         }

         const size = mainList.size;
         const total = fullList.length;

         setMainList({
            //@ts-ignore
            data: fullList?.slice((uiPage - 1) * size, uiPage * size),
            page: uiPage,
            size,
            total,
         });
      } catch (err) {
         Message.error("Fetching list error");
         console.error(err);
      }
   };

   const refreshList = () => fetchLists(page);

   const removeFromMainList = (listId: string) => {
      setMainList((prev) => ({
         ...prev,
         data: prev.data.filter((item) => item.listId !== listId),
         total: prev.total - 1,
      }));
   };

   useEffect(() => {
      setPage(1);
   }, [searchData]);

   useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const listId = params.get("listId");
      if (listId) {
         setSearchData({
            column: "listId",
            userInput: listId,
         });
      }
   }, []);

   useEffect(() => {
      fetchLists(page);
   }, [page, searchData]);

   return (
      <ListBuilderContext.Provider
         value={{
            mainList,
            setMainList,
            page,
            setPage,
            searchData,
            setSearchData,
            queryObject,
            setQueryObject,
            currentTab,
            setCurrentTab,
            refreshList,
            removeFromMainList,
         }}
      >
         {children}
      </ListBuilderContext.Provider>
   );
};
